// Core Modules
import { inject, Injectable } from '@angular/core';

// External Modules
import { CacheFactory } from 'cachefactory';

// Services
import { ConfigService } from '@core/services/config/config.service';

/**
 * Cache Service
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  // Injectors
  private readonly cacheFactory = inject(CacheFactory);
  private readonly configService = inject(ConfigService);

  // Public Members
  /**
   * cacheFactoryConfig to get cache factory config
   */
  public cacheFactoryConfig: Record<string, any> = this.configService.getContents('cacheFactoryConfig');

  /**
   * Setup cache factory for each key and return instance
   * @param key combination of request criteria and client id
   * @returns Returns instance of cache factory
   */
  public createCache(key: string): any {
    return this.cacheFactory.createCache(key, {
      maxAge: this.cacheFactoryConfig.cacheMaxAge, // e.g. 1 hour = 60 * 60 * 1000
      cacheFlushInterval: this.cacheFactoryConfig.cacheFlushInterval, // This cache will clear itself every hour
      deleteOnExpire: 'aggressive',
    });
  }

  /**
   * destroy cache from memory
   */
  public destroyAll(): void {
    this.cacheFactory.destroyAll();
  }

  /**
   * Clear cache by key
   * @param key key to destroy cache
   */
  public destroyCacheByKey(key: string): void {
    this.cacheFactory.destroy(key);
  }

  /**
   *
   * @param key combination of request criteria and client id
   * @returns Returns cached data by key
   */
  public getCacheData(key: string): any {
    return this.cacheFactory.get(key).get(key);
  }

  /**
   * Check for cache existence and returns flag
   * @param key combination of request criteria and client id
   * @returns undefined if key is exists else returns true
   */
  public isCacheExists(key: string): boolean {
    return this.cacheFactory.exists(key);
  }
}
